import { motion } from "framer-motion";
import { CheckCircle, Phone, ArrowRight, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const ThankYouPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2" data-testid="thankyou-logo-link">
              <div className="w-10 h-10 bg-[#D32F2F] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>JJ</span>
              </div>
              <span 
                className="hidden sm:block text-xl font-bold text-slate-900"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                buyshouses
              </span>
            </a>

            {/* Phone */}
            <a 
              href="tel:626-526-1420" 
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium"
              data-testid="thankyou-header-phone"
            >
              <Phone className="w-4 h-4" />
              <span>626-526-1420</span>
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-lg w-full text-center"
        >
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8"
          >
            <CheckCircle className="w-12 h-12 text-green-600" />
          </motion.div>

          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-3xl md:text-4xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Thank You!
          </motion.h1>

          {/* Message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <p className="text-lg text-slate-600 mb-6">
              Your request has been submitted successfully.
            </p>
            
            <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 mb-8">
              <div className="flex items-center gap-3 justify-center mb-4">
                <div className="w-10 h-10 bg-[#D32F2F]/10 rounded-full flex items-center justify-center">
                  <Phone className="w-5 h-5 text-[#D32F2F]" />
                </div>
                <h2 
                  className="text-xl font-semibold text-slate-900"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  What Happens Next?
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                One of our <span className="font-semibold text-slate-800">Acquisition Managers</span> will reach out to you <span className="font-semibold text-[#D32F2F]">within the next few hours</span> to discuss your property and present you with a fair cash offer.
              </p>
            </div>

            {/* Contact Info */}
            <div className="bg-slate-100 rounded-xl p-4 mb-8">
              <p className="text-sm text-slate-600 mb-2">
                Need immediate assistance? Call us directly:
              </p>
              <a 
                href="tel:626-526-1420" 
                className="inline-flex items-center gap-2 text-xl font-bold text-[#D32F2F] hover:text-[#B71C1C]"
                data-testid="thankyou-phone-link"
              >
                <Phone className="w-5 h-5" />
                626-526-1420
              </a>
            </div>

            {/* Back to Home */}
            <Link to="/">
              <Button 
                variant="outline"
                className="border-slate-300 hover:border-slate-400"
                data-testid="back-to-home-btn"
              >
                <Home className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </main>
    </div>
  );
};

export default ThankYouPage;
